import { ComponentRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DynamicsService, NotificationService } from '@seahorse/common';
import {
  CustomDataContentService,
  CustomDataSearchFieldModel,
  ExpenseEventType,
  ExpenseModel,
  ExpenseFilterModel,
  ExpensesDataService,
  FieldType,
  ObjectDefinitionHelpers,
  ObjectDefinitionModel,
  ProxyService,
  SearchOperators,
  DataObjectAddType,
  LinkedObjectsFieldRule,
} from '@seahorse/domain';
import { Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import * as _ from 'underscore';
import { DataCollectionConfig } from '../data/data-collection.model';
import { DataListComponent } from '../data/data-list/data-list.component';
import { DataField } from '../data/data.model';
import { CustomDataFormService } from '../form/custom-data-form.service';
import { FormField } from '../form/form.model';
import { ModalAction } from '../modal/modal.model';
import { ModalService } from '../modal/modal.service';
import { ExpenseUpdateModalHeader } from './expense-header.model';
import { ExpenseSearchHeaderComponent } from './expense-header/expense-search-header/expense-search-header.component';
import { ExpenseActionHeaderComponent } from './expense-header/expense-action-header/expense-action-header.component';

const DEFAULT_COLUMNS = [
  'description',
  'remarks',
  'supplier',
  'amount',
  'amountActual',
  'tariff',
];

export const expensesUpdateModalProcedure = (preload: boolean) => {
  const modalService = inject(ModalService);
  const objectDefinitionHelpers = inject(ObjectDefinitionHelpers);
  const customDataFormService = inject(CustomDataFormService);
  const objectDefinition = inject(ProxyService).objectDefinitions.find(
    (x) => x.mappingKey === '$customcontent_st-expense'
  );
  const customDataService = inject(CustomDataContentService);
  const expensesDataService = inject(ExpensesDataService);
  const notificationService = inject(NotificationService);
  const translate = inject(TranslateService);
  const dynamicsService = inject(DynamicsService);
  let dataListComponentInstance = null;
  let searchboxComponentRef: ComponentRef<ExpenseUpdateModalHeader> = null;
  let addDialogComponentRef: ComponentRef<ExpenseUpdateModalHeader> = null;
  let allExpenses: ExpenseModel[] = [];
  const hasSearchQuery = (): boolean => {
    return (
      searchboxComponentRef?.instance?.data !== undefined &&
      searchboxComponentRef?.instance?.data !== null &&
      searchboxComponentRef?.instance?.data !== ''
    );
  };

  return (
    skipService = false,
    expenses?: ExpenseModel[] | undefined,
    checkedIds?: string[],
    singleSelect = false,
    parent?: any,
    parentDefinition?: ObjectDefinitionModel,
    searchFields?: CustomDataSearchFieldModel[],
    headerComponentRef?: ComponentRef<ExpenseUpdateModalHeader>,
    showSearchBox = false
  ) => {
    const searchDefault = (searchFields: CustomDataSearchFieldModel[]) => {
      return customDataService
        .searchActiveCustomerDataByFields(
          'expense',
          searchFields,
          null,
          null,
          -1,
          -1,
          null,
          null,
          null,
          null,
          null,
          null,
          objectDefinitionHelpers.getConnectedDataConfig(objectDefinition)
        )
        .pipe(map((response) => response.result));
    };

    const data$ = preload ? searchDefault(searchFields) : of(expenses);

    const allFields = ObjectDefinitionModel.getAllFieldDefinitions(
      objectDefinition,
      true
    );

    const expensesLinkedField = parentDefinition?.objectFieldDefinitions?.find(
      (f) => f.systemCode === 'expenses'
    );
    const fieldRule = expensesLinkedField?.fieldRule as LinkedObjectsFieldRule;
    const searchConfig = fieldRule?.searchConfiguration;

    const displayFields = searchConfig?.displayFields?.length
      ? searchConfig.displayFields
      : DEFAULT_COLUMNS;

    const fields = displayFields
      .map((col) => {
        const field = allFields.find(
          (f) => f.systemCode?.toLowerCase() === col.toLowerCase()
        );
        if (field) {
          return DataField.fromFieldDefinition(field);
        }
        return null;
      })
      .filter((x) => x !== null);

    const poField: DataField = {
      key: 'po',
      name: 'PO',
      type: FieldType.SingleLineText,
    };

    fields.unshift(poField);

    const formModel = customDataFormService.getFormModel(
      objectDefinition,
      displayFields,
      null,
      false
    );

    const poFormField: FormField<FieldType.SingleLineText> = {
      key: 'po' as keyof FieldType.SingleLineText & string,
      name: 'PO',
      groupName: null,
      type: FieldType.SingleLineText,
      isRequired: false,
      isDisabled: true,
      validation: null,
      options: null,
    };

    formModel.fields.unshift(poFormField);

    // Reorder form fields so the edit mode follows the same order as displayFields,
    const order = new Map(displayFields.map((k, i) => [k.toLowerCase(), i]));
    formModel.fields = [
      poFormField,
      ...formModel.fields
        .filter((f) => f.key !== 'po')
        .sort(
          (a, b) =>
            order.get(a.key.toLowerCase()) - order.get(b.key.toLowerCase())
        ),
    ];

    const mapAndSetExpenses = (expenses: ExpenseModel[]) => {
      allExpenses = expenses.map((expense) => ({
        ...expense,
        po: expense.__AdditionalData?.orderRegistration?.po || null,
        isChecked: checkedIds?.includes(expense.__Id),
      }));
      return allExpenses;
    };

    const filterExpenses = (
      expenses: ExpenseModel[],
      filter: ExpenseUpdateModalHeader
    ) => {
      if (!filter || !filter.data || filter.type !== ExpenseEventType.Filter) {
        return expenses;
      }

      const eventData = filter.data as ExpenseFilterModel;
      const properties = eventData.propertyPaths.map((path) => {
        return path !== undefined && path !== null ? path.split('.') : [];
      });

      const tempList = allExpenses.filter((expense) => {
        let allMatch = true;
        properties.forEach((property) => {
          let current = expense;
          let isMatch = false;
          property.forEach((path) => {
            if (!path || current === undefined || current === null) return;

            current = current[path];
            if (current === undefined || current === null) {
              isMatch =
                isMatch ||
                eventData.filterValue === undefined ||
                eventData.filterValue === null;
            } else if (Array.isArray(current)) {
              if (
                eventData.filterValue === undefined ||
                eventData.filterValue === null
              ) {
                isMatch = isMatch || current.length === 0;
              } else {
                isMatch = isMatch || current.includes(eventData.filterValue);
              }
            } else if (typeof current === 'string') {
              isMatch = isMatch || current === eventData.filterValue;
            }
          });
          allMatch = allMatch && isMatch;
        });
        return allMatch;
      });

      return tempList;
    };

    const updateDataList = (
      expenses: ExpenseModel[],
      dataListComponentInstance: DataListComponent,
      resetCheckboxes = false,
      resetCheckboxesForNewExpenses = true
    ) => {
      if (dataListComponentInstance) {
        const seedData = objectDefinitionHelpers.getSeedData(
          parentDefinition?.systemCode,
          objectDefinition.addConfig
        );

        let isAllChecked = expenses && expenses.length > 0;
        if (resetCheckboxes === true) {
          expenses?.forEach((expense) => {
            expense['isChecked'] = false;
          });
          isAllChecked = false;
        } else {
          expenses?.forEach((expense) => {
            if (resetCheckboxesForNewExpenses === true) {
              const existing = dataListComponentInstance.items.find(
                (x) => x.__Id === expense.__Id
              );
              expense['isChecked'] = existing ? existing['isChecked'] : false;
            }
            isAllChecked = isAllChecked && expense['isChecked'];
          });
        }

        dataListComponentInstance.isAllChecked = isAllChecked;
        dataListComponentInstance.items = expenses;
        dataListComponentInstance.suggestedRows = expenses?.reduce(
          (acc, cur, index) => {
            acc[index] = objectDefinitionHelpers.applySeedData(
              parent,
              {},
              seedData
            );
            return acc;
          },
          {}
        );

        dataListComponentInstance.detectChanges();
      }
    };

    const handleData = (
      header: ExpenseUpdateModalHeader
    ): Observable<ExpenseModel[]> => {
      let search$: Observable<ExpenseModel[]>;
      const headerType = header?.type;
      let resetCheckboxes = false;
      let resetCheckboxesForNewExpenses = true;
      switch (headerType) {
        case ExpenseEventType.Search:
          {
            resetCheckboxes = true;
            header.isLoading = true;
            const fields = hasSearchQuery()
              ? objectDefinition.simpleSearchConfig?.fields.map(
                  (field) =>
                    ({
                      fieldName: field,
                      searchOperator: SearchOperators.ContainsAny,
                      searchValue: header.data,
                    } as CustomDataSearchFieldModel)
                )
              : searchFields;

            search$ = searchDefault(fields);
          }
          break;
        case ExpenseEventType.Filter:
          search$ = of(allExpenses);
          break;
        case ExpenseEventType.Add:
          if (header.data) {
            resetCheckboxesForNewExpenses = false;
            const newExpense = header.data as ExpenseModel;
            if (!checkedIds) {
              checkedIds = [];
            }
            checkedIds.push(newExpense.__Id);

            if (!allExpenses) {
              allExpenses = [];
            }
            allExpenses.unshift(newExpense);
          }
          search$ = of(allExpenses);
          break;
        default:
          search$ = of(allExpenses);
          break;
      }

      return search$.pipe(
        map((expenses) => {
          mapAndSetExpenses(expenses);
          objectDefinitionHelpers.applyClientSideSort(
            allExpenses,
            searchConfig?.sortFields || []
          );
          return filterExpenses(allExpenses, headerComponentRef?.instance);
        }),
        tap((filteredExpenses) => {
          if (header) {
            header.isLoading = false;
          }
          updateDataList(
            filteredExpenses,
            dataListComponentInstance,
            resetCheckboxes,
            resetCheckboxesForNewExpenses
          );
        })
      );
    };

    if (headerComponentRef) {
      headerComponentRef.changeDetectorRef.detectChanges();
      headerComponentRef.instance.filterChange.subscribe((headerResponse) =>
        handleData(headerResponse).subscribe()
      );
    }

    if (showSearchBox) {
      searchboxComponentRef = dynamicsService.createComponent({
        component: ExpenseSearchHeaderComponent,
      });
      searchboxComponentRef.changeDetectorRef.detectChanges();
      searchboxComponentRef.instance.filterChange.subscribe((headerResponse) =>
        handleData(headerResponse).subscribe()
      );
    }

    if (objectDefinition?.addConfig?.type == DataObjectAddType.Anywhere) {
      const addDialogComponent = dynamicsService.createComponent({
        component: ExpenseActionHeaderComponent,
      });
      addDialogComponent.changeDetectorRef.detectChanges();
      addDialogComponent.instance.parent = parent;
      addDialogComponent.instance.parentDefinition = parentDefinition;
      addDialogComponent.instance.objectDefinition = objectDefinition;
      addDialogComponent.instance.filterChange.subscribe((headerResponse) =>
        handleData(headerResponse).subscribe()
      );
      addDialogComponentRef = addDialogComponent;
    }

    return data$.pipe(
      mergeMap((data) => {
        mapAndSetExpenses(data);
        objectDefinitionHelpers.applyClientSideSort(
          allExpenses,
          searchConfig?.sortFields || []
        );
        const items = filterExpenses(allExpenses, headerComponentRef?.instance); // check if header component is defined and filter the data,
        const seedData = objectDefinitionHelpers.getSeedData(
          parentDefinition?.systemCode,
          objectDefinition.addConfig
        );

        const modalRef = modalService
          .buildModal()
          .withTitle('customModules.expenses.updateDialog')
          .withHTMLHeader(searchboxComponentRef?.location.nativeElement)
          .withHTMLHeader(headerComponentRef?.location.nativeElement)
          .withHTMLHeader(addDialogComponentRef?.location.nativeElement)
          .withComponentBody<DataListComponent>({
            component: DataListComponent,
            props: {
              selectable: true,
              singleSelect: singleSelect,
              items,
              suggestedRows: items.reduce((acc, cur, index) => {
                acc[index] = objectDefinitionHelpers.applySeedData(
                  parent,
                  {},
                  seedData
                );
                return acc;
              }, {}),
              config: <DataCollectionConfig>{
                objectDefinition: objectDefinition,
                displayFields: fields,
              },
              formModel,
              editModeTrigger: 'checkbox',
              showFormActions: false,
            },
            hostAttributes: ['flex'],
          });

        dataListComponentInstance = modalRef.components[0] as DataListComponent;
        dataListComponentInstance.checkChange.subscribe();
        dataListComponentInstance.detectChanges();

        return modalRef.open({ size: 'xl', isTable: true }).pipe(
          mergeMap((res) => {
            if (res.action === ModalAction.Close) {
              return of(undefined);
            }

            const dataListRef = res.componentRef as DataListComponent;
            const updatedExpenses = dataListRef.forms.map((x) => x.value);

            res.modalRef.close();

            if (skipService) {
              return of(updatedExpenses);
            }

            return expensesDataService.update(updatedExpenses).pipe(
              tap(
                (rExpenses) => {
                  if (rExpenses.hasResult) {
                    notificationService.showSuccess(
                      'customModules.expenses.expenseUpdated',
                      'shared.terms.success'
                    );
                  } else {
                    notificationService.showError(
                      _.pluck(rExpenses.messages, 'message').join('\n'),
                      translate.instant('shared.terms.failed')
                    );
                  }
                },
                (eExpenses) => {
                  notificationService.showError(
                    _.pluck(eExpenses.error.messages, 'message').join('\n'),
                    translate.instant('shared.terms.failed')
                  );
                }
              ),
              map((response) => response.result)
            );
          })
        ) as Observable<ExpenseModel[] | undefined>;
      })
    );
  };
};
